import { useTaxSummary } from '../../hooks/useReports';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Sparkles, ArrowDownLeft, ArrowUpRight, Scale, FileSpreadsheet, Printer } from 'lucide-react';

export function TaxSummary({ startDate, endDate }: { startDate: string; endDate: string }) {
  const { data, isLoading, error } = useTaxSummary(startDate, endDate);

  const formatCurrency = (val: number) =>
    `₹${(Math.abs(val) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (isLoading) {
    return (
      <div className="space-y-4 py-4">
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 text-center text-rose-600 bg-rose-50 dark:bg-rose-950/30 rounded-2xl border border-rose-200 dark:border-rose-900 text-sm font-medium">
        Failed to load GST & TDS Tax Summary.
      </div>
    );
  }

  const taxAccounts = data.taxAccounts || [];
  const totalNetBalance = data.totalNetBalance || 0;
  const isNetPayable = totalNetBalance >= 0;

  // Calculate gross output tax vs gross input credit
  let outputTaxTotal = 0;
  let inputCreditTotal = 0;
  taxAccounts.forEach((acc) => {
    if (acc.normalBalance === 'credit') {
      outputTaxTotal += acc.netBalance || 0;
    } else {
      inputCreditTotal += acc.netBalance || 0;
    }
  });

  const exportCSV = () => {
    const rows = [
      ['Tax Summary Report (GST & TDS)', `Period: ${startDate} to ${endDate}`],
      [''],
      ['ACCOUNT CODE', 'TAX ACCOUNT NAME', 'TYPE', 'NET BALANCE (INR)'],
      ...taxAccounts.map((b) => [
        b.code,
        b.name,
        b.normalBalance === 'debit' ? 'Input Credit (Receivable)' : 'Output Tax (Payable)',
        b.netBalance,
      ]),
      ['NET TAX PAYABLE / (REFUND)', '', '', totalNetBalance],
    ];

    const csvContent = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Tax_Summary_${startDate}_to_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* ── Top Tax KPI Cards ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Output Tax Payable */}
        <div className="bg-white dark:bg-slate-950 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-purple-600/10 text-purple-600 dark:bg-purple-500/20 dark:text-purple-400 flex items-center justify-center shrink-0">
            <ArrowUpRight className="w-5 h-5" />
          </div>
          <div>
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Output Tax Payable
            </span>
            <p className="text-lg font-black font-mono text-purple-700 dark:text-purple-300 mt-0.5">
              {formatCurrency(outputTaxTotal)}
            </p>
            <p className="text-[10px] text-slate-400 font-medium mt-0.5">Tax on Outward Sales</p>
          </div>
        </div>

        {/* Input Tax Credit (ITC) */}
        <div className="bg-white dark:bg-slate-950 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-blue-600/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400 flex items-center justify-center shrink-0">
            <ArrowDownLeft className="w-5 h-5" />
          </div>
          <div>
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Input Tax Credit (ITC)
            </span>
            <p className="text-lg font-black font-mono text-blue-700 dark:text-blue-300 mt-0.5">
              {formatCurrency(inputCreditTotal)}
            </p>
            <p className="text-[10px] text-slate-400 font-medium mt-0.5">Tax Credit on Purchases</p>
          </div>
        </div>

        {/* Net Tax Position Card */}
        <div
          className={`p-4 rounded-2xl border shadow-sm flex items-center justify-between gap-3 ${
            isNetPayable
              ? 'bg-rose-50/80 dark:bg-rose-950/30 border-rose-200 dark:border-rose-900/50 text-rose-900 dark:text-rose-200'
              : 'bg-emerald-50/80 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900/50 text-emerald-900 dark:text-emerald-200'
          }`}
        >
          <div>
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Net Tax Position</span>
            </div>
            <p className="text-xl font-black font-mono mt-1">
              {totalNetBalance < 0 ? `(${formatCurrency(totalNetBalance)})` : formatCurrency(totalNetBalance)}
            </p>
            <p className="text-[10px] font-medium mt-0.5">
              {isNetPayable ? 'Net Tax Amount Due to Govt' : 'Net ITC Surplus Carryforward'}
            </p>
          </div>
          <button
            onClick={exportCSV}
            className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 transition-colors text-xs font-semibold flex items-center gap-1 shrink-0"
            title="Download CSV"
          >
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
          </button>
        </div>
      </div>

      {/* ── Tax Accounts Breakdown Table ─────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden space-y-0">
        <div className="p-4 bg-slate-50/70 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Scale className="w-4 h-4 text-purple-600" />
            <span>GST & Statutory Tax Accounts Ledger</span>
          </h3>
          <button
            onClick={() => window.print()}
            className="px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 transition-colors flex items-center gap-1 text-[11px] font-semibold"
          >
            <Printer className="w-3.5 h-3.5" />
            <span>Print Report</span>
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="bg-slate-50/80 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 font-bold text-xs uppercase tracking-wide">
              <tr>
                <th className="px-6 py-3.5 w-36">Code</th>
                <th className="px-6 py-3.5">Tax Account Name</th>
                <th className="px-6 py-3.5 w-48 text-center">Tax Category</th>
                <th className="px-6 py-3.5 text-right w-52">Net Tax Liability / Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-900">
              {taxAccounts.map((b) => {
                let liability = 0;
                if (b.normalBalance === 'credit') liability = b.netBalance;
                if (b.normalBalance === 'debit') liability = -b.netBalance;

                const isDebitInput = b.normalBalance === 'debit';

                return (
                  <tr key={b.accountId} className="hover:bg-slate-50/60 dark:hover:bg-slate-900/40 transition-colors">
                    <td className="px-6 py-3 font-mono text-xs font-semibold">
                      <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200/60 dark:border-slate-800 text-slate-700 dark:text-slate-300">
                        {b.code}
                      </span>
                    </td>
                    <td className="px-6 py-3 font-bold text-slate-900 dark:text-slate-100 text-xs">
                      {b.name}
                    </td>
                    <td className="px-6 py-3 text-center">
                      <span
                        className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full font-semibold border ${
                          isDebitInput
                            ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                            : 'bg-purple-50 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 border-purple-200 dark:border-purple-800'
                        }`}
                      >
                        {isDebitInput ? 'Input Credit (Receivable)' : 'Output Tax (Payable)'}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right font-mono text-xs font-bold">
                      <span className={liability >= 0 ? 'text-purple-700 dark:text-purple-300' : 'text-emerald-700 dark:text-emerald-400'}>
                        {liability < 0 ? `(${formatCurrency(liability)})` : formatCurrency(liability)}
                      </span>
                    </td>
                  </tr>
                );
              })}

              {taxAccounts.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-12 text-center text-xs text-slate-400 font-medium">
                    No tax accounts configured in Chart of Accounts with GST or TDS tags.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot className="bg-slate-50 dark:bg-slate-900 border-t-2 border-slate-200 dark:border-slate-800 font-black text-xs">
              <tr>
                <td colSpan={3} className="px-6 py-4 text-right uppercase tracking-wider text-slate-700 dark:text-slate-300">
                  Total Net Tax Payable / (Refund Surplus):
                </td>
                <td className={`px-6 py-4 text-right font-mono text-sm ${totalNetBalance >= 0 ? 'text-rose-700 dark:text-rose-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
                  {totalNetBalance < 0 ? `(${formatCurrency(totalNetBalance)})` : formatCurrency(totalNetBalance)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
